import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { renderClose } from "../../common/dialog";
import { UseEntityStore, createEntityStore } from "../../common/entityStore";
import { useEffect, useState } from "react";
import { AddressInputProps, useAddressSuggestions } from "../../common/addressSuggestions";
import { useDefaultValues } from "../../common/defaultValues";
import { propEnumField, propTextField } from "../../common/entityStoreForm";
import { CustomerSexTypeId, useCustomerSexTypes, useDeliveryMethods } from "../../common/enum";
import { SaveButton } from "../../common/SaveButton";
import { getCustomerAddressBaseValidators } from "../customer/validators";
import { CustomerAddressDto, TmOrderDto } from "./common";
import { getResource } from "../../common/localization";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Customer." + key, defaultMessage) as string;
}

export const NewAddressId = -1;

export function CustomerAddressDialog(props: {
    close: () => void;
    customerAddress: CustomerAddressDto | null | undefined;
    useCustomerStore: UseEntityStore<TmOrderDto>;
    disabled: boolean;
}) {
    const [useAddressStore] = useState(() => createEntityStore<CustomerAddressDto>({
        data: props.customerAddress ?? {
            id: NewAddressId,
            customerSexTypeId: CustomerSexTypeId.Unknown,
            title: "",
            fname: "",
            lname: "",
            strt1: "",
            strt2: "",
            zipCode: "",
            city: "",
            coadr: "",
            mob: "",
            tel: "",
            email: "",
        },
        id: props.customerAddress?.id ?? null,
        getNewId: p => p.id!,
        saveProps: ["customerSexTypeId", "title", "fname", "lname",
            "strt1", "strt2", "zipCode", "city", "coadr", "mob", "tel", "email"],
        fetchOnInit: false,
        isValidationEnabled: true,
        path: "customers/customerAddresses",
        qs: { customerId: props.useCustomerStore.getState().serverData.customerId },
        onSaved() {
            props.useCustomerStore.getState().fetchServerData();
            props.close();
        }
    }));
    const addressStore = useAddressStore(p => ({
        entityId: p.entityId,
        isSaveDisabled: p.isSaveDisabled,
    }));
    const address = useAddressSuggestions(useAddressStore);
    const def = useDefaultValues(p => p.clientData);
    const deliveryMethods = useDeliveryMethods();

    const addressInputProps: AddressInputProps = { disabled: props.disabled };
    useEffect(() => useAddressStore.getState().setPropValidators(getCustomerAddressBaseValidators(def.landCode, true, deliveryMethods.items.find(d => d.isDefault && d.code === "DPD"))), [useAddressStore, deliveryMethods, def.landCode]);
    function importFromCustomerDetails() {
        const customer = props.useCustomerStore.getState().clientData;
        const addressStore = useAddressStore.getState();
        addressStore.setClientDataProp("customerSexTypeId", customer.customerSexTypeId);
        addressStore.setClientDataProp("title", customer.title);
        addressStore.setClientDataProp("fname", customer.fname);
        addressStore.setClientDataProp("lname", customer.lname);
        addressStore.setClientDataProp("strt1", customer.strt1);
        addressStore.setClientDataProp("strt2", customer.strt2);
        addressStore.setClientDataProp("zipCode", customer.zipCode);
        addressStore.setClientDataProp("city", customer.city);
        addressStore.setClientDataProp("coadr", customer.coadr);
        addressStore.setClientDataProp("mob", customer.mob);
        addressStore.setClientDataProp("tel", customer.tel);
        addressStore.setClientDataProp("email", customer.email);
    }
    function save() {
        useAddressStore.getState().enableValidation();
        if (useAddressStore.getState().isSaveDisabled) {
            return;
        }
        var address = useAddressStore.getState().clientData;
        const addresses = (props.useCustomerStore.getState().clientData.customerAddresses ?? [])
            .filter(p => p.id !== address.id)
            .concat(address);
        props.useCustomerStore.getState().setClientDataProp("customerAddresses", addresses);
        if (address.id === NewAddressId) {
            props.useCustomerStore.getState().setClientDataProp("deliveryAddressId", address.id);
        }
        props.close();
    }
    return <>
        <DialogTitle>{getText("DeliveryAddress")}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={4}>{propEnumField(useAddressStore)("customerSexTypeId", getText("Gender"), useCustomerSexTypes, { disabled: props.disabled })}</Grid>
                <Grid item xs={4}></Grid>
                <Grid item xs={4}></Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("title", getText("Title"), { disabled: props.disabled })}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("fname", getText("FirstName"), { disabled: props.disabled })}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("lname", getText("LastName"), { disabled: props.disabled })}</Grid>

                <Grid item xs={12}>{propTextField(useAddressStore)("coadr", getText("CoAdr"), { disabled: props.disabled })}</Grid>

                <Grid item xs={8}>{address.renderStreetInput(addressInputProps)}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("strt2", getText("Strt2"), { disabled: props.disabled })}</Grid>

                <Grid item xs={4}>{address.renderZipInput(addressInputProps)}</Grid>
                <Grid item xs={8}>{address.renderCityInput(addressInputProps)}</Grid>

                <Grid item xs={4}>{propTextField(useAddressStore)("tel", getText("Tel"), { disabled: props.disabled })}</Grid>
                <Grid item xs={4}>{propTextField(useAddressStore)("mob", getText("Mob"), { disabled: props.disabled })}</Grid>
                <Grid item xs={4} />

                <Grid item xs={12}>{propTextField(useAddressStore)("email", getText("Email"), { disabled: props.disabled })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={importFromCustomerDetails} disabled={props.disabled}>{getText("DeliveryAddress.ImportFromCustomer")}</Button>
            <SaveButton save={save} disabled={addressStore.isSaveDisabled} text={props.customerAddress == null ? getResource("Ui.Add") as string : getResource("Ui.Update") as string} />
            <Button onClick={props.close} color="negative" variant="outlined">{getResource("Ui.Close")}</Button>
        </DialogActions>
    </>;
}
